import React from 'react';
import { CheckCircle2, Lock, MessageCircle } from 'lucide-react';
import { useI18n } from '../../i18n/I18nContext';
import { formatRelative } from '../../utils/datetime';
import { pluralKey } from '../../utils/plural';
import StatusBadge from '../StatusBadge/StatusBadge';
import './ThreadList.css';

function truncate(text, max) {
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
}

// A thread's state as a tone: the icon chip, the badge and the label all follow it.
const STATUS = {
  ANSWERED: { tone: 'success', Icon: CheckCircle2, labelKey: 'qaThreads.statusAnswered' },
  CLOSED: { tone: 'neutral', Icon: Lock, labelKey: 'qaThreads.statusClosed' },
  OPEN: { tone: 'warning', Icon: MessageCircle, labelKey: 'qaThreads.statusUnanswered' },
};

// Note: classnames here are namespaced with a `qtl-` prefix (QA Thread List).
// CRA loads every page's CSS into one global stylesheet, and generic names
// like `.thread-item`/`.thread-title` are already reused by src/pages/teacher/
// TeacherQA.css — an unprefixed class here would silently pick up whichever
// file's rules happen to load later.

/**
 * The questions of a course as one card of hairline-separated rows (spec §3.6): a tone chip for
 * the state, the question, who asked it and when, and on the right how many answers it has and its
 * state as a badge. The whole row opens the thread.
 */
function ThreadList({ threads, onSelectThread }) {
  const { t, lang } = useI18n();

  return (
    <div className="section-card qtl-list ui-list--feed">
      {threads.map((thread) => {
        const preview = truncate(thread.body, 120);
        const status = STATUS[thread.status] || STATUS.OPEN;
        const { Icon } = status;
        const replies = thread.replies?.length || 0;
        const asked = thread.askedAt || thread.createdAt;
        return (
          <div
            key={thread.id}
            className={`ui-list-row is-interactive qtl-item ${thread.status === 'ANSWERED' ? 'qtl-item--answered' : 'qtl-item--unanswered'}`}
            role="button"
            tabIndex={0}
            aria-label={t('qaThreads.openThread', { title: preview })}
            onClick={() => onSelectThread(thread)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelectThread(thread);
              }
            }}
          >
            <span className="ui-icon-chip ui-icon-chip--md" data-tone={status.tone} aria-hidden="true">
              <Icon />
            </span>

            <div className="ui-list-row__body qtl-content">
              <h3 className="qtl-title">{preview}</h3>
              <div className="qtl-meta">
                <span className="qtl-author">{thread.authorName || t('qaThreads.anonymous')}</span>
                {asked && (
                  <>
                    <span className="qtl-sep" aria-hidden="true">·</span>
                    <span className="qtl-time">{formatRelative(asked, { lang })}</span>
                  </>
                )}
                {thread.lessonName && <span className="qtl-lesson ds-badge ds-badge-neutral">{thread.lessonName}</span>}
              </div>
            </div>

            <div className="qtl-stats">
              <span className="qtl-replies">{t(pluralKey('qaThreads.replyCount', replies, lang), { count: replies })}</span>
              <StatusBadge tone={status.tone}>{t(status.labelKey)}</StatusBadge>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default ThreadList;
